import { InfoCircleOutlined } from '@ant-design/icons';
import { Collapse, Spin, Tooltip } from 'antd';
import EmploymentContractsView from '@/components/employment-contracts/EmploymentContractsView';
import HolidayHistoryView from '@/components/holiday-history/HolidayHistoryView';
import OtherLeavesHistoryView from '@/components/other-leaves-history/OtherLeavesHistoryView';
import { PERMISSIONS, ROLES } from '@/constants';
import PrivateAction from '@/routes/PrivateAction';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

/** Annual leave summary and the holiday actions. */
export default function EmployeeHolidaySummary() {
  const {
    calculationData,
    calculationLoading,
    employeeId,
    employmentType,
    isEdited,
    isHolidayTrackerCompany,
    isProfile,
    t,
  } = useEmployeeGeneralDataForm();

  // On the profile: laid out by EmployeeHolidayProfilePanel.
  if (isProfile) return null;

  return (
    <>
      <hr className="my-5" />

      <div className="grid gap-2">
        <p>{t('workHolidaySummary', { ns: 'messages' })}</p>
        <div className="rounded border border-zinc-200 bg-white p-2">
          <ul className="grid gap-2 divide-y [&_li]:pt-2 first:[&_li]:pt-0 [&_li_div]:flex [&_li_div]:items-center [&_li_div]:justify-between [&_li_div]:rounded [&_li_div]:px-1 [&_li_div]:py-1 hover:[&_li_div]:bg-[rgba(var(--accentColor),0.1)]">
            {[
              {
                label: t('totalHolidayDaysLeftYearEnd', { ns: 'messages' }),
                value: calculationData?.calculation?.numberOfHolidayDays,
                showTooltip: false,
              },
              calculationData?.calculation
                ?.numberOfHolidayDaysByEndOfContract && {
                label: t('totalDaysLeftLimitedContract', { ns: 'messages' }),
                value:
                  calculationData?.calculation
                    ?.numberOfHolidayDaysByEndOfContract,
                showTooltip: false,
              },
              {
                label: t('currentlyAvailableHolidayDays', { ns: 'messages' }),
                value:
                  calculationData?.calculation?.currentlyAvailableHolidayDays,
                showTooltip: true,
              },
            ]
              .filter(Boolean)
              .map(({ label, value, showTooltip }, index) => (
                <li key={index}>
                  <Tooltip
                    placement="topLeft"
                    title={
                      showTooltip
                        ? t('currentlyAvailableHolidayDaysInfo', {
                            ns: 'messages',
                          })
                        : null
                    }
                  >
                    <div className="flex justify-between">
                      <span>
                        {showTooltip && (
                          <InfoCircleOutlined className="mr-2 text-base text-zinc-400" />
                        )}

                        <span>{label}:</span>
                      </span>

                      {calculationLoading ? (
                        <span className="flex h-8 w-8 animate-pulse items-center justify-center rounded bg-zinc-200">
                          <Spin />
                        </span>
                      ) : (
                        <strong className="flex h-8 w-8 items-center justify-center rounded border border-zinc-300 bg-white py-0.5">
                          {value ?? '~'}
                        </strong>
                      )}
                    </div>
                  </Tooltip>
                </li>
              ))}
          </ul>
        </div>
      </div>

      {/* On the profile page the contracts and both histories have their
            own tiles (rendered by the page), so they are not repeated here. */}
      {isEdited && employeeId && !isProfile && (
        <PrivateAction
          requiredRoles={[
            ROLES.superAdmin,
            ROLES.techAdmin,
            ROLES.admin,
            ROLES.client,
            ROLES.clientOfficer,
            ROLES.hrOfficer,
          ]}
          requiredPermissions={[PERMISSIONS.employeeView]}
        >
          <Collapse
            className="mt-4"
            items={[
              {
                key: 'employment-contracts',
                label: t('employmentContracts.title', { ns: 'translations' }),
                children: (
                  <EmploymentContractsView
                    employeeId={employeeId}
                    canEdit
                    profileEmploymentType={employmentType}
                  />
                ),
              },
            ]}
          />
        </PrivateAction>
      )}

      {isEdited && employeeId && isHolidayTrackerCompany && !isProfile && (
        <PrivateAction
          requiredRoles={[
            ROLES.superAdmin,
            ROLES.techAdmin,
            ROLES.admin,
            ROLES.client,
            ROLES.clientOfficer,
            ROLES.hrOfficer,
          ]}
          requiredPermissions={[PERMISSIONS.employeeView]}
        >
          <Collapse
            className="mt-4"
            items={[
              {
                key: 'go-history',
                label: t('holidayHistory.title', { ns: 'translations' }),
                children: <HolidayHistoryView employeeId={employeeId} />,
              },
            ]}
          />

          <Collapse
            className="mt-4"
            items={[
              {
                key: 'other-leaves-history',
                label: t('otherLeavesHistory.title', { ns: 'translations' }),
                children: <OtherLeavesHistoryView employeeId={employeeId} />,
              },
            ]}
          />
        </PrivateAction>
      )}
    </>
  );
}
